import { Navigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { CheckCircle2, ArrowRight, Phone, MessageCircle } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import { SERVICE_PAGES } from "@/data/servicePages";
import { serviceSchema, breadcrumbSchema, faqSchema } from "@/lib/seo";
import { CTA, PRICE_GUIDE, PRICING_NOTE, SCOPE_STATEMENT } from "@/lib/business";
import { prefillFor } from "@/lib/prefill";
import { waHref, telHref, CONTACT } from "@/lib/contact";
import { trackEvent } from "@/lib/analytics";

export default function ServicePage({ slug }) {
  const data = SERVICE_PAGES[slug];

  useEffect(() => {
    if (data) trackEvent("view_service", { service: slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!data) return <Navigate to="/404" replace />;

  const path = `/${slug}`;
  const breadcrumbs = [{ name: data.breadcrumb, path }];
  const jsonLd = [
    serviceSchema({ name: data.h1, description: data.description, serviceType: data.serviceType, areaServed: data.areaServed, path }),
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: data.breadcrumb, path }]),
    faqSchema(data.faqs),
  ];

  return (
    <PageShell breadcrumbs={breadcrumbs} quoteContext={prefillFor(path)}>
      {({ onQuote }) => (
        <>
          <Seo title={data.title} description={data.description} path={path} jsonLd={jsonLd} />

          {/* Intro */}
          <section className="wrap pt-6 pb-12 lg:pt-10 lg:pb-16">
            <span className="eyebrow">Independent property inspection · UAE</span>
            <h1 className="text-[clamp(2rem,4vw,3rem)] leading-[1.06] tracking-[-0.03em] mt-3 mb-5 max-w-[20ch]">{data.h1}</h1>
            <p className="text-[1.1rem] text-bodytext max-w-[62ch] mb-7">{data.intro}</p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button onClick={onQuote} data-testid="service-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                {CTA.primary} <ArrowRight size={17} />
              </button>
              <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-wa hover:text-wa transition-colors">
                <MessageCircle size={17} /> {CTA.whatsapp}
              </a>
            </div>
          </section>

          {data.image && (
            <section className="wrap pb-12 lg:pb-16">
              <figure className={`overflow-hidden rounded-2xl border border-line bg-soft ${data.image.portrait ? "max-w-[440px] mx-auto" : ""}`}>
                <picture>
                  <source
                    type="image/avif"
                    srcSet={data.image.widths.map((w) => `${data.image.base}-${w}.avif ${w}w`).join(", ")}
                    sizes={data.image.portrait ? "(max-width: 480px) 100vw, 440px" : "(max-width: 1152px) 100vw, 1088px"}
                  />
                  <source
                    type="image/webp"
                    srcSet={data.image.widths.map((w) => `${data.image.base}-${w}.webp ${w}w`).join(", ")}
                    sizes={data.image.portrait ? "(max-width: 480px) 100vw, 440px" : "(max-width: 1152px) 100vw, 1088px"}
                  />
                  <img
                    src={`${data.image.base}-${data.image.widths[0]}.webp`}
                    alt={data.image.alt}
                    width={data.image.w}
                    height={data.image.h}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-cover"
                    data-testid="service-image"
                  />
                </picture>
              </figure>
            </section>
          )}

          <div className="wrap grid lg:grid-cols-2 gap-x-14 gap-y-12 pb-14">
            <Block title="What we inspect"><List items={data.whatWeInspect} /></Block>
            <Block title="Who this is for"><List items={data.whoFor} /></Block>
            <Block title="Property types">
              <div className="flex flex-wrap gap-2">
                {data.propertyTypes.map((p) => (
                  <span key={p} className="px-3 py-1.5 rounded-full border border-line text-sm text-bodytext bg-soft">{p}</span>
                ))}
              </div>
            </Block>
            <Block title="Equipment we use"><List items={data.equipment} /></Block>
            <Block title="The inspection process">
              <ol className="space-y-3">
                {data.process.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex-none w-6 h-6 rounded-full bg-blue text-white grid place-items-center text-xs font-bold">{i + 1}</span>
                    <span className="text-bodytext">{step}</span>
                  </li>
                ))}
              </ol>
            </Block>
            <Block title="What you receive"><List items={data.deliverables} /></Block>
          </div>

          {/* Scope */}
          <section className="wrap pb-14">
            <div className="bg-soft border border-line rounded-2xl p-6 lg:p-8 max-w-[80ch]">
              <h2 className="text-xl mb-3">Honest scope &amp; limitations</h2>
              <p className="text-bodytext mb-3">{SCOPE_STATEMENT}</p>
              <p className="text-bodytext">{data.scopeLimits}</p>
              {data.access && <p className="text-bodytext mt-3" data-testid="service-access">{data.access}</p>}
            </div>
          </section>

          {/* Pricing */}
          <section className="wrap pb-14">
            <h2 className="text-2xl mb-3">Pricing</h2>
            {data.priceMode === "commercial" ? (
              <>
                <div className="bg-soft border border-line rounded-2xl p-6 lg:p-8 max-w-[62ch]">
                  <div className="text-sm text-muted mb-1">Commercial inspection</div>
                  <div className="font-display text-blue text-2xl mb-3" data-testid="service-price">Fixed quote after scope review</div>
                  <p className="text-bodytext text-[0.95rem]">The fee is based on the property type, size, location, access conditions and agreed inspection scope. Fedari confirms the exact fixed fee before the booking is accepted.</p>
                </div>
              </>
            ) : (
              <>
                {data.priceNote && <p className="text-bodytext max-w-[62ch] mb-4" data-testid="service-price-note">{data.priceNote}</p>}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-[80ch]">
                  {PRICE_GUIDE.map((row) => (
                    <div key={row.type} className="border border-line rounded-xl p-4 bg-white">
                      <div className="text-sm text-muted mb-1">{row.type}</div>
                      <div className="font-display text-blue">{row.price}</div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted max-w-[64ch] mt-4">{PRICING_NOTE}</p>
              </>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <button onClick={onQuote} className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">{CTA.primary}</button>
              <a href={telHref} onClick={() => trackEvent("click_phone", { location: `service-${slug}` })} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold hover:border-blue"><Phone size={16} /> {CONTACT.phoneDisplay}</a>
            </div>
          </section>

          {/* FAQ */}
          <section className="wrap pb-14">
            <h2 className="text-2xl mb-5">Frequently asked questions</h2>
            <div className="space-y-4 max-w-[80ch]">
              {data.faqs.map((f, i) => (
                <div key={i} className="border border-line rounded-xl p-5">
                  <h3 className="font-semibold text-ink mb-2">{f.q}</h3>
                  <p className="text-bodytext text-[0.95rem]">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related */}
          <section className="wrap pb-16">
            <h2 className="text-xl mb-4">Related inspections</h2>
            <div className="flex flex-wrap gap-3 mb-8">
              {data.related.map((r) => (
                <Link key={r.href} to={r.href} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-line text-ink font-medium hover:border-blue hover:text-blue transition-colors">
                  {r.label} <ArrowRight size={15} />
                </Link>
              ))}
            </div>
            <h2 className="text-xl mb-4">Useful guides</h2>
            <div className="flex flex-wrap gap-3">
              {(SERVICE_GUIDES[slug] || SERVICE_GUIDES.default).map(([label, href]) => (
                <Link key={href} to={href} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-line text-ink font-medium hover:border-blue hover:text-blue transition-colors">
                  {label} <ArrowRight size={15} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

// Topic-cluster links: every service page links to its most relevant guides.
const SERVICE_GUIDES = {
  "handover-inspection-dubai": [["When to book an inspection", "/guides/when-to-book-snagging-inspection"], ["Snagging vs handover vs DLP", "/guides/snagging-vs-handover-vs-dlp"], ["Inside a snagging report", "/guides/property-snagging-report-dubai"]],
  "dlp-warranty-inspection-dubai": [["The 11-month DLP inspection", "/guides/dlp-warranty-11-month-inspection"], ["Sending your report to the developer", "/guides/send-snagging-report-to-developer"], ["Snagging vs handover vs DLP", "/guides/snagging-vs-handover-vs-dlp"]],
  "resale-property-inspection-dubai": [["When to book an inspection", "/guides/when-to-book-snagging-inspection"], ["Room-by-room checklist", "/guides/room-by-room-snagging-checklist"], ["Inside a snagging report", "/guides/property-snagging-report-dubai"]],
  "villa-inspection-dubai": [["Villa vs apartment snagging", "/guides/villa-vs-apartment-snagging"], ["Room-by-room checklist", "/guides/room-by-room-snagging-checklist"], ["Waterproofing & moisture problems", "/guides/waterproofing-moisture-problems-uae"]],
  "apartment-inspection-dubai": [["Villa vs apartment snagging", "/guides/villa-vs-apartment-snagging"], ["Room-by-room checklist", "/guides/room-by-room-snagging-checklist"], ["Snagging costs in 2026", "/guides/snagging-cost-dubai-2026"]],
  "reinspection-dubai": [["Sending your report to the developer", "/guides/send-snagging-report-to-developer"], ["The 11-month DLP inspection", "/guides/dlp-warranty-11-month-inspection"], ["Inside a snagging report", "/guides/property-snagging-report-dubai"]],
  "services/overseas-property-inspection-dubai": [["When to book an inspection", "/guides/when-to-book-snagging-inspection"], ["What is snagging?", "/guides/what-is-snagging-dubai"], ["Inside a snagging report", "/guides/property-snagging-report-dubai"]],
  "property-inspection-uae": [["What is snagging?", "/guides/what-is-snagging-dubai"], ["Snagging costs in 2026", "/guides/snagging-cost-dubai-2026"], ["Room-by-room checklist", "/guides/room-by-room-snagging-checklist"]],
  default: [["What is snagging?", "/guides/what-is-snagging-dubai"], ["Inside a snagging report", "/guides/property-snagging-report-dubai"], ["Room-by-room checklist", "/guides/room-by-room-snagging-checklist"]],
};

function Block({ title, children }) {
  return (
    <section>
      <h2 className="text-xl mb-4">{title}</h2>
      {children}
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-bodytext">
          <CheckCircle2 size={18} className="text-blue flex-none mt-0.5" aria-hidden="true" /> {it}
        </li>
      ))}
    </ul>
  );
}
